import { Deal } from '../types/deal';

/**
 * Resolve a deal image reference (Firebase Storage path, gs:// ref, or plain
 * URL) into a usable download URL, caching the result in localStorage under the
 * same key `useFirebaseImageUrl` reads. Returns '' if it can't be resolved.
 *
 * Shared by the hook (in-component) and the prefetch path (pre-render warming).
 */
export async function resolveStorageUrl(path?: string): Promise<string> {
  if (!path) return '';

  const cacheKey = `firebase_logo_${path}`;
  const cached = localStorage.getItem(cacheKey);
  if (cached) return cached;

  const isStorageRef =
    path.startsWith('gs://') ||
    path.startsWith('https://firebasestorage') ||
    !path.startsWith('http');

  if (!isStorageRef) {
    localStorage.setItem(cacheKey, path);
    return path;
  }

  try {
    let storagePath = path;

    if (path.startsWith('gs://')) {
      storagePath = path.split('/').slice(3).join('/');
    } else if (path.startsWith('https://firebasestorage')) {
      const parsed = new URL(path);
      const match = parsed.pathname.match(/\/o\/(.+)$/);
      if (match) {
        storagePath = decodeURIComponent(match[1]);
      } else {
        localStorage.setItem(cacheKey, path);
        return path;
      }
    }

    const { getStorage, ref, getDownloadURL } = await import('firebase/storage');
    const downloadUrl = await getDownloadURL(ref(getStorage(), storagePath));
    localStorage.setItem(cacheKey, downloadUrl);
    return downloadUrl;
  } catch (error) {
    console.error('[storageUrl] Failed to resolve image:', path, error);
    return '';
  }
}

/**
 * Resolve a path then kick off the browser image download so the bytes are warm
 * before React renders the page.
 */
const prefetchImage = async (path?: string): Promise<void> => {
  const url = await resolveStorageUrl(path);
  if (url) {
    const img = new Image();
    img.src = url;
  }
};

/**
 * Pre-warm every asset a deal can show across the promo pages, as soon as the
 * deal is known (before the page that needs it mounts). Fire-and-forget.
 */
export const prefetchDealAssets = (deal: Deal | null | undefined): void => {
  if (!deal) return;
  const paths = [
    deal.authImage,
    deal.welcomeImage,
    deal.images?.[0],
    deal.logo,
    deal.callout?.icon,
  ];
  paths.forEach((p) => { if (p) void prefetchImage(p); });
};
